import React from 'react';
import ReactDom from 'react-dom';
import { Provider } from 'react-redux';
import { Button } from 'carbon-components-react';
import { Add16, Subtract16, SendAlt16 } from '@carbon/icons-react';
import { isBrowser } from 'react-device-detect';
import { BaseMapSwitcherButton } from 'components/BaseMapSwitcher';
import { configureStore as store } from 'redux/store';
import { zoomToCenter } from 'components/Map/helper';

const CURRENT_LOC_CONTAINER_CLASS = 'map--current-loc-button-container';
const BASEMAP_CONTAINER_CLASS = 'map--basemap-container';

const onClickCurrentLocation = () => {
  navigator.geolocation.getCurrentPosition((position) => {
    const center = [position.coords.longitude, position.coords.latitude];
    zoomToCenter(center);
  });
};

const CurrentLocButton: React.FC = () => (
  <Button
    kind="tertiary"
    size="small"
    renderIcon={SendAlt16}
    iconDescription="My current location"
    tooltipPosition="right"
    hasIconOnly
    className="map--current-loc-button"
    onClick={onClickCurrentLocation}
  />
);

interface MountedControl {
  container: HTMLDivElement;
}

let mountedControls: MountedControl[] = [];

function getZoomWidgetDomNode(view: __esri.MapView): HTMLElement | null {
  const zoomUi: any = view.ui.find('zoom');
  return zoomUi?.domNode ?? null;
}

/**
 * Mounts zoom, current-location, and map-style controls on a member lookup MapView.
 * Mirrors the control setup in components/Map for visual and behavioural parity.
 */
export function setupMemberLookupMapUi(view: __esri.MapView): void {
  view.ui.padding = 0;

  if (isBrowser) {
    view.ui.move('zoom', 'bottom-left');
  } else {
    view.ui.remove('zoom');
  }

  const currentLocContainer = document.createElement('div');
  currentLocContainer.className = CURRENT_LOC_CONTAINER_CLASS;
  view.ui.add(currentLocContainer, 'top-left');
  mountedControls.push({ container: currentLocContainer });

  const basemapContainer = document.createElement('div');
  basemapContainer.className = BASEMAP_CONTAINER_CLASS;
  view.ui.add(basemapContainer, 'bottom-left');
  mountedControls.push({ container: basemapContainer });

  view.when(() => {
    if (isBrowser) {
      const zoomDomNode = getZoomWidgetDomNode(view);
      const zoomInButton = zoomDomNode
        ?.getElementsByClassName('esri-icon-plus')
        .item(0);
      const zoomOutButton = zoomDomNode
        ?.getElementsByClassName('esri-icon-minus')
        .item(0);
      zoomInButton && ReactDom.render(<Add16 />, zoomInButton);
      zoomOutButton && ReactDom.render(<Subtract16 />, zoomOutButton);
    }

    ReactDom.render(<CurrentLocButton />, currentLocContainer);
    ReactDom.render(
      <Provider store={store}>
        <BaseMapSwitcherButton />
      </Provider>,
      basemapContainer
    );
  });
}

export function teardownMemberLookupMapUi(): void {
  mountedControls.forEach(({ container }) => {
    ReactDom.unmountComponentAtNode(container);
  });
  mountedControls = [];
}
